/**
 * Telegram Mini App Authentication Service
 *
 * Handles Telegram InitData and sends it to the server for verification.
 */

const TELEGRAM_SDK_URL = 'https://telegram.org/js/telegram-web-app.js';
let sdkLoadPromise = null;

/** Load Telegram SDK only when running inside Telegram (not on every page load). */
export const loadTelegramSdk = () => {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.Telegram?.WebApp) return Promise.resolve(true);
  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = TELEGRAM_SDK_URL;
    script.async = true;
    script.onload = () => resolve(Boolean(window.Telegram?.WebApp));
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
  return sdkLoadPromise;
};

export const isLikelyTelegramEnvironment = () => {
  if (typeof window === 'undefined') return false;
  return (
    /Telegram/i.test(navigator.userAgent) ||
    window.location.search.includes('tgWebApp') ||
    window.location.hash.includes('tgWebAppData')
  );
};

// Check if Telegram WebApp SDK is available
export const isTelegramWebApp = () => {
  return typeof window !== 'undefined' && window.Telegram?.WebApp;
};

// Get Telegram WebApp instance
export const getTelegramWebApp = () => {
  if (!isTelegramWebApp()) {
    console.warn('Telegram WebApp not available');
    return null;
  }
  return window.Telegram.WebApp;
};

/**
 * Retrieve raw InitData (for server verification)
 * @returns {string|null} Raw initData string or null
 */
export const getRawInitData = () => {
  try {
    const tg = getTelegramWebApp();
    if (!tg) {
      console.warn('⚠️ Telegram WebApp instance not available');
      return null;
    }
    
    // Read raw initData from Telegram SDK (sent to server for verification)
    const initData = tg.initData;
    
    if (!initData) {
      console.warn('⚠️ InitData not available in Telegram WebApp');
      // Try alternative methods
      if (tg.initDataUnsafe) {
        console.log('ℹ️ Trying to get initData from initDataUnsafe');
      }
    }
    
    return initData || null;
  } catch (error) {
    console.error('❌ Error getting raw initData:', error);
    return null;
  }
};

/**
 * Read user info from unsafe InitData (client-side only)
 * NOTE: Not verified — use for UI display only
 * @returns {Object|null} User object or null
 */
export const getUnsafeUserData = () => {
  try {
    const tg = getTelegramWebApp();
    if (!tg) return null;
    
    // User data from initDataUnsafe
    const user = tg.initDataUnsafe?.user;
    return user || null;
  } catch (error) {
    console.error('Error getting unsafe user data:', error);
    return null;
  }
};

/**
 * Complete InitData object (client-side, not verified)
 * @returns {Object|null} Complete initData object
 */
export const getInitDataUnsafe = () => {
  try {
    const tg = getTelegramWebApp();
    if (!tg) return null;
    
    return tg.initDataUnsafe || null;
  } catch (error) {
    console.error('Error getting initDataUnsafe:', error);
    return null;
  }
};

/**
 * Detect Telegram Mini App environment
 * @returns {boolean} True when running inside Telegram
 */
export const isInTelegram = () => {
  return isTelegramWebApp();
};

/**
 * Expand Telegram WebApp (fullscreen mode)
 */
export const expandTelegramApp = () => {
  const tg = getTelegramWebApp();
  if (tg) {
    tg.expand();
  }
};

/**
 * Detect Telegram WebApp theme
 * @returns {string} 'light' or 'dark'
 */
export const getTelegramTheme = () => {
  const tg = getTelegramWebApp();
  if (tg) {
    return tg.colorScheme || 'light';
  }
  return 'light';
};

/**
 * Send data to the bot (Mini App → bot message)
 * @param {string} data - Data string to send to the bot
 */
export const sendDataToBot = (data) => {
  const tg = getTelegramWebApp();
  if (tg && tg.sendData) {
    tg.sendData(data);
  } else {
    console.warn('sendData method not available');
  }
};

/**
 * Send authentication request to the backend
 * @param {string} rawInitData - Raw initData string
 * @param {string} apiEndpoint - Backend API endpoint
 * @returns {Promise} API response
 */
export const authenticateWithTelegram = async (rawInitData, apiEndpoint) => {
  try {
    console.log('📡 Sending authentication request...');
    console.log('📍 Endpoint:', apiEndpoint);
    console.log('🔐 Authorization header length:', `tma ${rawInitData}`.length);
    
    const requestHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `tma ${rawInitData}` // Format: "tma <rawInitData>"
    };
    
    console.log('📋 Request headers:', {
      'Content-Type': requestHeaders['Content-Type'],
      'Authorization': `tma ${rawInitData.substring(0, 50)}...`
    });

    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: requestHeaders
    });

    console.log('📥 Response status:', response.status);
    console.log('📥 Response ok:', response.ok);

    if (!response.ok) {
      const errorData = await response.json().catch(() => {
        // If JSON parsing fails, try to get text
        return response.text().then(text => ({ error: text || `HTTP ${response.status}` }));
      });
      console.error('❌ Response error:', errorData);
      throw new Error(errorData.error || `Authentication failed: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('✅ Response data received:', data);
    return data;
  } catch (error) {
    console.error('❌ Telegram authentication fetch error:', error);
    console.error('❌ Error name:', error.name);
    console.error('❌ Error message:', error.message);
    if (error.cause) {
      console.error('❌ Error cause:', error.cause);
    }
    throw error;
  }
};
